import { useState } from 'react';
import { AlertTriangle, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DiscardChangesDialog } from '@/components/settings/DiscardChangesDialog';

interface SettingsSaveBarProps {
  /** Whether there are unsaved changes. Cancel/Save are disabled when false. */
  isDirty: boolean;
  /** Whether a save is currently in progress. */
  isSaving?: boolean;
  /** Called when Save is clicked. */
  onSave: () => void;
  /** Called when the user confirms discarding pending changes. */
  onDiscard: () => void;
  /**
   * Left offset for the sticky footer so it aligns with the app sidebar.
   * Defaults to 0 (full width) for screens that are not offset by a sidebar.
   */
  sidebarLeft?: string;
}

/**
 * Shared sticky save/cancel footer for Settings screens.
 *
 * Renders a fixed bottom-right bar with Cancel and Save buttons (disabled until
 * dirty), a saving spinner, and the amber "You have unsaved changes" indicator.
 * Clicking Cancel while dirty opens a "Discard unsaved changes?" confirmation
 * dialog; confirming calls `onDiscard`.
 */
export function SettingsSaveBar({
  isDirty,
  isSaving = false,
  onSave,
  onDiscard,
  sidebarLeft = '0',
}: SettingsSaveBarProps) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const handleCancel = () => {
    if (isDirty) {
      setShowCancelDialog(true);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelDialog(false);
    onDiscard();
  };

  return (
    <>
      <div
        className="fixed bottom-0 right-0 z-20 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-[left] duration-200 ease-linear"
        style={{ left: sidebarLeft }}
      >
        <div className="flex h-16 items-center justify-between gap-4 px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {isDirty && (
              <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" />
                You have unsaved changes
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              disabled={!isDirty || isSaving}
              onClick={handleCancel}
              size="lg"
              className="min-w-[100px]"
            >
              Cancel
            </Button>
            <Button
              disabled={!isDirty || isSaving}
              onClick={onSave}
              size="lg"
              className="min-w-[100px]"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <DiscardChangesDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onConfirm={handleConfirmCancel}
      />
    </>
  );
}

export default SettingsSaveBar;
